import * as React from 'react'
import { FunctionComponent, ReactElement, useMemo, useRef, useState } from 'react'
import EmailList, { EmailItem, EmailType } from '../../components/List/Email/EmailList'
import { Message, Recipient } from '@microsoft/microsoft-graph-types'
import { DataPromise, Destination, IGraphArrayPayload } from '../../context/AxiosContext'
import { getTheme, IList, MarqueeSelection, Selection, SelectionZone } from '@fluentui/react'
import { IFileDto } from '../../config/fileConfig'
import EmailSearchBox from '../../components/List/Email/EmailSearchBox'
import { ISelection, SelectionMode } from '@fluentui/react/lib/Selection'
import { DefaultError } from '../Diagnostics/Errors'
import { NormalizedError } from '../../reducers/errorReducer'
import { useForceUpdate } from '@fluentui/react-hooks'
import { SelectableEmail } from '../../hooks/email/useProcessEmail'
import { makeStyles, MessageBar, shorthands, tokens } from '@fluentui/react-components'
import { EmailToolbarProps } from '../Toolbar/Email/EmailToolbar'
import { Interval } from '../Toolbar/IntervalToolbarMenu'
import { useTranslation } from 'react-i18next'
import { extractReferenceNumbersFromMessage } from '../../helpers/referenceNumberHelper'
import { Host } from '../../context/AuthorizationContext'

export type Props = {
    emailMailbox: string
    interval: Interval
    setInterval: (interval: Interval) => void
    messages: DataPromise<IGraphArrayPayload<Message>>
    items: EmailItem[]
    hasItems: boolean
    isLoading: boolean
    setIsLoading: (loading: boolean) => void
    reset: () => void // Callback for when the view needs to be updated.
    showLocation: boolean
    file?: DataPromise<IFileDto>
    toolbar?: FunctionComponent<EmailToolbarProps>
    searchable?: boolean
}

export enum MailDirection {
    In = 'Inbound',
    Out = 'Outbound',
    Both = 'Both',
}

export const MailDirectTypeMap = new Map([
    [MailDirection.In, EmailType.In],
    [MailDirection.Out, EmailType.Out],
])

const formatEmail = ({ emailAddress: { name, address } }: Recipient): string => (name === address ? address : `${name} (${address})`)

const isSameEmailDomain = (email: string, otherEmail: Recipient): boolean =>
    email
        ?.substring(email.indexOf('@'))
        .localeCompare(otherEmail?.emailAddress?.address?.substring(otherEmail?.emailAddress?.address?.indexOf('@'))) === 0

const mapMessage = (message: Message, currentMailbox: string, currentUser: string, host: Host, destination: Destination): EmailItem => ({
    id: message.id,
    internetMessageId: message.internetMessageId,
    conversationId: message.conversationId,
    recipient:
        message.sender?.emailAddress.address === currentUser
            ? message.toRecipients.map(formatEmail).join('; ')
            : message.sender && formatEmail(message.sender),
    recipients: message.toRecipients,
    sender: message.sender,
    destination: destination,
    type: isSameEmailDomain(currentUser, message.sender) ? EmailType.Out : EmailType.In,
    subject: message.subject,
    snippet: message.bodyPreview,
    date: message.receivedDateTime,
    hasAttachment: message.hasAttachments,
    references: extractReferenceNumbersFromMessage(message),
    url: `${process.env.INBOXER_HOST}/api/download-message?hostKey=${host?.RowKey}&email=${encodeURIComponent(currentMailbox)}&messageId=${message.id}`,
})

const theme = getTheme()

const useStyles = makeStyles({
    alert: {
        ...shorthands.margin(tokens.spacingHorizontalS),
    },
    container: {
        display: 'grid',
    },
    actionItems: {
        position: 'sticky',
        top: '40px',
        zIndex: 8,
        backgroundColor: tokens.colorNeutralBackground3,
        borderBottom: `1px solid ${theme.palette.themeLighter}`,
    },
})

export const mapMessages = (messages: Message[], currentMailbox: string, currentUser: string, host: Host, destination: Destination): EmailItem[] =>
    messages
        .filter((message: Message) => !message.isDraft)
        .map((message: Message) => mapMessage(message, currentMailbox, currentUser, host, destination))

export default function EmailSearchSelectList(props: Props): ReactElement {
    const styles = useStyles()
    const { t: translate } = useTranslation()

    const forceUpdate = useForceUpdate()
    const [error, setError] = useState<NormalizedError>()
    const [direction, setDirection] = useState<MailDirection>(MailDirection.Both)
    const [allowedMessageIds, setAllowedMessageIds] = useState<string[]>()
    const componentRef = useRef<IList>()
    const [isSelectionPreservedOnEmptyClick, setSelectionPreservedOnEmptyClick] = useState<boolean>(false)

    const items: EmailItem[] = useMemo(() => {
        if (props.items) {
            return (props.items || [])
                .filter(message => !allowedMessageIds || allowedMessageIds.includes(message.id))
                .filter(message => direction === MailDirection.Both || MailDirectTypeMap.get(direction) === message.type)
        }
        return items
    }, [props.items, allowedMessageIds, direction, props.interval])

    const selection = useMemo(
        () =>
            new Selection<SelectableEmail>({
                items,
                onSelectionChanged: () => {
                    componentRef.current?.forceUpdate()
                    forceUpdate()
                },
                getKey: item => item.id,
                selectionMode: SelectionMode.multiple,
            }),
        [items, componentRef.current],
    )

    return (
        <div className={styles.container}>
            {error && <DefaultError error={error} dismiss={() => setError(undefined)} />}
            {props.messages.rejected && (
                <MessageBar intent="error" className={styles.alert}>
                    {props.file
                        ? translate('file.pivot.email.error.no_access')
                        : translate('email.filing.error.mailbox.no_access', { email: props.emailMailbox })}
                </MessageBar>
            )}
            {props.file && !props.isLoading && props.items?.length === 0 && (
                <MessageBar intent="info" className={styles.alert}>
                    {translate('file.pivot.email.error.no_content')}
                </MessageBar>
            )}
            {!props.file && !props.isLoading && props.items?.length === 0 && (
                <MessageBar intent="info" className={styles.alert}>
                    {translate('email.filing.error.mailbox.no_content')}
                </MessageBar>
            )}
            <div className={styles.actionItems}>
                {props.searchable === true && (
                    <EmailSearchBox
                        setAllowedMessageIds={setAllowedMessageIds}
                        reference={props.file?.data?.reference}
                        disabled={!props.hasItems || props.isLoading || props.messages.rejected}
                        onClear={() => setAllowedMessageIds(undefined)}
                        onSearch={() => props.setIsLoading(true)}
                        setIsLoading={props.setIsLoading}
                    />
                )}
                {
                    <props.toolbar
                        {...props}
                        direction={direction}
                        setDirection={setDirection}
                        selection={selection}
                        setAllowedMessageIds={setAllowedMessageIds}
                        setPreventDefaultEvent={setSelectionPreservedOnEmptyClick}
                    />
                }
            </div>
            <MarqueeSelection selection={selection as ISelection} isEnabled={true}>
                <SelectionZone selection={selection as ISelection} selectionPreservedOnEmptyClick={isSelectionPreservedOnEmptyClick}>
                    <EmailList
                        items={items}
                        componentRef={componentRef}
                        isLoading={props.isLoading}
                        hasMessages={props.hasItems}
                        showLocation={props.showLocation}
                        selection={selection}
                    />
                </SelectionZone>
            </MarqueeSelection>
        </div>
    )
}
